import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { addLaserToCart } from "../actions/cart";
import { API_BASE } from "../helpers/api";

const MAX_BYTES = 25 * 1024 * 1024;
const LASER_ACCEPTED = [".dxf", ".svg", ".png", ".jpg", ".jpeg", ".webp"];

export default function LaserCutting({ process = "laser" }) {
  const isPlasma = process === "plasma";
  const serviceName = isPlasma ? "Plasma cutting" : "Laser cutting";
  const acceptedFiles = isPlasma ? [".dxf"] : LASER_ACCEPTED;
  const dispatch = useDispatch();
  const fileSelectionId = useRef(0);
  const [csrfToken, setCsrfToken] = useState("");
  const [materials, setMaterials] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [dxfPreview, setDxfPreview] = useState("");
  const [aspectRatio, setAspectRatio] = useState(null);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [form, setForm] = useState({
    material: "mild-steel",
    thickness_mm: "1",
    width_mm: "200",
    height_mm: "100",
    quantity: "1",
    design_hours: "0",
    finishing_hours: "0",
    express: false,
    email: "",
    notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState("");
  const [analysingDimensions, setAnalysingDimensions] = useState(false);
  const [dimensionMessage, setDimensionMessage] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/csrf/`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setCsrfToken(data.csrfToken))
      .catch(() => {});
    fetch(`${API_BASE}/${process}/options/`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        const nextMaterials = data.materials || [];
        setMaterials(nextMaterials);
        setForm((current) => {
          const selected =
            nextMaterials.find((item) => item.id === current.material) || nextMaterials[0];
          if (!selected) return { ...current, material: "", thickness_mm: "" };
          const thicknesses = selected.thicknesses || [];
          const currentThickness = Number(current.thickness_mm);
          const thickness = thicknesses.includes(currentThickness)
            ? currentThickness
            : thicknesses[0];
          return {
            ...current,
            material: selected.id,
            thickness_mm: thickness == null ? "" : String(thickness),
          };
        });
      })
      .catch(() => {});
  }, [process]);

  useEffect(
    () => () => {
      if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.id === form.material),
    [materials, form.material],
  );
  const isImagePreview = file && /\.(svg|png|jpe?g|webp)$/i.test(file.name);
  const isDxf = file && /\.dxf$/i.test(file.name);

  const update = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "material") {
        const material = materials.find((item) => item.id === value);
        if (material?.thicknesses?.length) {
          next.thickness_mm = String(material.thicknesses[0]);
        }
      }
      if (
        lockAspectRatio &&
        aspectRatio &&
        !isDxf &&
        (name === "width_mm" || name === "height_mm")
      ) {
        const dimension = Number(value);
        if (Number.isFinite(dimension) && dimension > 0) {
          const linkedValue = name === "width_mm" ? dimension / aspectRatio : dimension * aspectRatio;
          next[name === "width_mm" ? "height_mm" : "width_mm"] = String(
            Math.round(linkedValue * 1000) / 1000,
          );
        }
      }
      return next;
    });
    setQuote(null);
    setMessage("");
  };

  const chooseFile = async (nextFile) => {
    const selectionId = ++fileSelectionId.current;
    setError("");
    setQuote(null);
    setDimensionMessage("");
    setDxfPreview("");
    setAspectRatio(null);
    if (!nextFile) return;
    const ext = `.${nextFile.name.split(".").pop().toLowerCase()}`;
    if (!acceptedFiles.includes(ext)) {
      setError(isPlasma ? "Upload a DXF file." : "Upload a DXF, SVG, PNG, JPG or WEBP file.");
      return;
    }
    if (nextFile.size > MAX_BYTES) {
      setError("File is larger than the 25MB limit.");
      return;
    }
    setFile(nextFile);
    const nextPreview = URL.createObjectURL(nextFile);
    setPreview(nextPreview);
    if (ext !== ".dxf") {
      const previewImage = new Image();
      previewImage.onload = () => {
        if (selectionId !== fileSelectionId.current) return;
        if (previewImage.naturalWidth > 0 && previewImage.naturalHeight > 0) {
          const ratio = previewImage.naturalWidth / previewImage.naturalHeight;
          setAspectRatio(ratio);
          setForm((current) => {
            const width = Number(current.width_mm) || 200;
            return {
              ...current,
              height_mm: String(Math.round((width / ratio) * 1000) / 1000),
            };
          });
        }
      };
      previewImage.src = nextPreview;
    }
    if (ext === ".dxf") {
      if (!csrfToken) {
        setError("The upload service is still initialising. Please select the DXF again.");
        return;
      }
      setAnalysingDimensions(true);
      setForm((current) => ({ ...current, width_mm: "", height_mm: "" }));
      try {
        const body = new FormData();
        body.append("file", nextFile);
        const response = await fetch(`${API_BASE}/${process}/analyse/`, {
          method: "POST",
          credentials: "include",
          headers: { "X-CSRFToken": csrfToken },
          body,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || "Unable to measure this DXF.");
        setForm((current) => ({
          ...current,
          width_mm: String(data.width_mm),
          height_mm: String(data.height_mm),
        }));
        if (data.preview_svg) {
          setDxfPreview(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(data.preview_svg)}`);
        }
        setDimensionMessage(
          data.assumed_mm
            ? `Drawing extents detected: ${data.width_mm} × ${data.height_mm} mm. The DXF has no unit setting, so millimetres were assumed.`
            : `Drawing extents detected: ${data.width_mm} × ${data.height_mm} mm.`,
        );
      } catch (err) {
        setError(err.message || "Unable to measure this DXF.");
      } finally {
        setAnalysingDimensions(false);
      }
    }
  };

  const getQuote = async () => {
    if (!file || !csrfToken) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const body = new FormData();
      body.append("file", file);
      Object.entries(form).forEach(([key, value]) =>
        body.append(key, String(value)),
      );
      const response = await fetch(`${API_BASE}/${process}/upload/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.detail || "Unable to prepare this quote.");
      setQuote(data);
      if (/\.dxf$/i.test(file.name)) {
        setForm((current) => ({
          ...current,
          width_mm: String(data.width_mm),
          height_mm: String(data.height_mm),
        }));
      }
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    setAdding(true);
    setError("");
    const ok = await dispatch(addLaserToCart(quote?.id));
    setAdding(false);
    if (ok) setMessage(`${serviceName} quote added to your cart.`);
    else setError("Could not add this quote to your cart.");
  };

  const fieldClass =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white";

  return (
    <div className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-2xl">
      <section className="relative border-b border-white/10 px-6 py-14 md:px-12">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, #f97316 0, transparent 28%), linear-gradient(120deg, transparent 55%, #334155 100%)",
          }}
        />
        <div className="relative max-w-3xl">
          <h1 className="text-4xl font-black leading-tight md:text-6xl">
            {isPlasma ? "Big profiles. Cut to shape." : "Your idea. Cut in metal."}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            {isPlasma
              ? "Local plasma profile cutting for brackets, plates, repairs and fabrication parts up to 1200 × 1200 mm. Upload your drawing for an instant Australian-dollar estimate."
              : "Fast, local laser cutting for one-offs, prototypes, signs, artwork, and hobby or automotive parts. Upload your drawing and get an instant Australian-dollar estimate."}
          </p>
          <div className="mt-7 flex flex-wrap gap-3 text-sm font-semibold text-slate-200">
            {[
              "No minimum quantity",
              "One or two parts welcome",
              "Fast turnaround",
            ].map((text) => (
              <span
                key={text}
                className="rounded-full border border-white/15 bg-white/5 px-4 py-2"
              >
                {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="bg-slate-900 p-6 md:p-10">
          <h2 className="text-2xl font-bold">1. Upload your design</h2>
          <div
            onDrop={(e) => {
              e.preventDefault();
              chooseFile(e.dataTransfer.files?.[0]);
            }}
            onDragOver={(e) => e.preventDefault()}
            className="mt-5 rounded-2xl border-2 border-dashed border-slate-600 bg-slate-950/60 p-8 text-center transition hover:border-orange-500"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-500/15 text-2xl text-orange-400">
              ↑
            </div>
            <p className="font-semibold">Drop your file here</p>
            <p className="mt-1 text-sm text-slate-400">
              {isPlasma ? "DXF only" : "DXF, SVG, PNG, JPG or WEBP"} · up to 25MB
            </p>
            <label className="mt-5 inline-flex cursor-pointer rounded-lg bg-orange-500 px-5 py-2.5 font-bold text-slate-950 hover:bg-orange-400">
              Choose file
              <input
                type="file"
                className="sr-only"
                accept={acceptedFiles.join(",")}
                onChange={(e) => chooseFile(e.target.files?.[0])}
              />
            </label>
          </div>
          {file && (
            <div className="mt-5 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
              {isImagePreview || (isDxf && dxfPreview) ? (
                <img
                  src={isDxf ? dxfPreview : preview}
                  alt="Uploaded design preview"
                  className={`h-64 w-full object-contain p-4 ${isDxf ? "bg-black" : ""}`}
                />
              ) : (
                <div className="flex h-48 items-center justify-center text-center">
                  <div>
                    <div className="text-5xl text-orange-400">⌁</div>
                    <p className="mt-2 font-bold">DXF drawing ready</p>
                  </div>
                </div>
              )}
              <p className="border-t border-slate-800 px-4 py-3 text-sm text-slate-300">
                {file.name} · {Math.max(1, Math.round(file.size / 1024))} KB
              </p>
            </div>
          )}
          <div className="mt-8 rounded-xl border border-orange-500/30 bg-orange-500/10 p-5 text-sm text-orange-100">
            {isPlasma ? (
              <><strong>DXF drawings only.</strong> Make sure the drawing contains closed, cut-ready profiles at the required finished size.</>
            ) : (
              <><strong>Image or logo?</strong> We can trace and clean it up. Add the expected design time so your estimate includes CAD work.</>
            )}
            <p className="mt-3 border-t border-orange-500/20 pt-3">
              Don&apos;t see the material or thickness you want?{" "}
              <Link to="/contact" className="font-bold underline hover:text-white">
                Contact us
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="bg-slate-50 p-6 text-slate-900 dark:bg-slate-800 dark:text-white md:p-10">
          <h2 className="text-2xl font-bold">2. Build your estimate</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <label className="text-sm font-semibold">
              Material
              <select
                className={`${fieldClass} mt-2`}
                value={form.material}
                onChange={(e) => update("material", e.target.value)}
              >
                {!materials.length && <option value="">No materials available</option>}
                {materials.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-semibold">
              Thickness
              <select
                className={`${fieldClass} mt-2`}
                value={form.thickness_mm}
                onChange={(e) => update("thickness_mm", e.target.value)}
              >
                {!selectedMaterial?.thicknesses?.length && (
                  <option value="">No thicknesses available</option>
                )}
                {(selectedMaterial?.thicknesses || []).map(
                  (value) => (
                    <option key={value} value={value}>
                      {value} mm
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="text-sm font-semibold">
              Finished width (mm)
              <input
                className={`${fieldClass} mt-2`}
                type="number"
                min="1"
                max="1200"
                value={form.width_mm}
                onChange={(e) => update("width_mm", e.target.value)}
                readOnly={isDxf}
              />
              {isDxf && <span className="mt-1 block text-xs font-normal text-orange-600 dark:text-orange-300">Read from DXF extents</span>}
            </label>
            <label className="text-sm font-semibold">
              Finished height (mm)
              <input
                className={`${fieldClass} mt-2`}
                type="number"
                min="1"
                max="1200"
                value={form.height_mm}
                onChange={(e) => update("height_mm", e.target.value)}
                readOnly={isDxf}
              />
              {isDxf && <span className="mt-1 block text-xs font-normal text-orange-600 dark:text-orange-300">Read from DXF extents</span>}
            </label>
            {isImagePreview && (
              <label className="flex items-center gap-3 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold dark:border-slate-600 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={lockAspectRatio}
                  onChange={(e) => setLockAspectRatio(e.target.checked)}
                  className="h-4 w-4 accent-orange-500"
                />
                Lock width and height to the uploaded image ratio
                {aspectRatio && (
                  <span className="ml-auto text-xs font-normal text-slate-500 dark:text-slate-300">
                    {aspectRatio.toFixed(3)}:1
                  </span>
                )}
              </label>
            )}
            <label className="text-sm font-semibold">
              Quantity
              <input
                className={`${fieldClass} mt-2`}
                type="number"
                min="1"
                max="100"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
              />
            </label>
            <label className="text-sm font-semibold">
              CAD/design time
              <select
                className={`${fieldClass} mt-2`}
                value={form.design_hours}
                onChange={(e) => update("design_hours", e.target.value)}
              >
                <option value="0">Ready to cut · A$0</option>
                <option value="0.25">Minor cleanup · 15 min</option>
                <option value="0.5">Tracing / repair · 30 min</option>
                <option value="1">Design help · 1 hour</option>
              </select>
            </label>
            <label className="text-sm font-semibold">
              Finishing
              <select
                className={`${fieldClass} mt-2`}
                value={form.finishing_hours}
                onChange={(e) => update("finishing_hours", e.target.value)}
              >
                <option value="0">As cut · A$0</option>
                <option value="0.25">Light deburr · 15 min</option>
                <option value="0.5">Deburr & sand · 30 min</option>
                <option value="1">Detailed finish · 1 hour</option>
              </select>
            </label>
            <label className="text-sm font-semibold">
              Email (optional)
              <input
                className={`${fieldClass} mt-2`}
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
              />
            </label>
          </div>
          <label className="mt-5 block text-sm font-semibold">
            Job notes
            <textarea
              className={`${fieldClass} mt-2`}
              rows="3"
              placeholder="Tolerances, edge finish, bend or tapping requirements…"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </label>
          <label className="mt-5 flex items-center gap-3 rounded-xl border border-slate-300 p-4 dark:border-slate-600">
            <input
              type="checkbox"
              checked={form.express}
              onChange={(e) => update("express", e.target.checked)}
              className="h-5 w-5 accent-orange-500"
            />
            <span>
              <strong>Express turnaround</strong>
              <span className="block text-xs text-slate-500 dark:text-slate-300">
                Target under 24 hours · +25%
              </span>
            </span>
          </label>

          {error && (
            <div className="mt-5 rounded-lg bg-red-100 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
              {error}
            </div>
          )}
          {analysingDimensions && (
            <div className="mt-5 rounded-lg bg-orange-100 px-4 py-3 text-sm text-orange-800 dark:bg-orange-950 dark:text-orange-200">
              Measuring the DXF drawing extents…
            </div>
          )}
          {dimensionMessage && !analysingDimensions && (
            <div className="mt-5 rounded-lg bg-green-100 px-4 py-3 text-sm text-green-800 dark:bg-green-950 dark:text-green-200">
              {dimensionMessage}
            </div>
          )}
          <button
            onClick={getQuote}
            disabled={!file || !csrfToken || loading || analysingDimensions || !form.width_mm || !form.height_mm}
            className="mt-6 w-full rounded-xl bg-orange-500 px-6 py-4 text-lg font-black text-slate-950 transition hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? "Calculating…" : "Get instant estimate"}
          </button>

          {quote && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-600 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-orange-500">
                    Estimate #{quote.id}
                  </p>
                  <h3 className="mt-1 text-3xl font-black">
                    A${(quote.price_cents / 100).toFixed(2)}
                  </h3>
                </div>
                <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700">
                  AUD estimate
                </span>
              </div>
              <div className="mt-5 space-y-2 border-y border-slate-200 py-4 text-sm dark:border-slate-700">
                {[
                  ["Material", quote.breakdown.material],
                  ["Machine time", quote.breakdown.machine],
                  ["CAD/design", quote.breakdown.design],
                  ["Finishing", quote.breakdown.finishing],
                  ["Express", quote.breakdown.express],
                ]
                  .filter(([, value]) => value > 0)
                  .map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-300">
                        {label}
                      </span>
                      <span>A${value.toFixed(2)}</span>
                    </div>
                  ))}
                {quote.breakdown.minimum_applied && (
                  <p className="pt-2 text-xs text-slate-500">
                    A$20 minimum job charge applied.
                  </p>
                )}
              </div>
              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                Instant estimates are subject to a drawing and manufacturability
                review before production.
              </p>
              <div className="mt-5 flex flex-wrap items-start gap-4">
                <div className="flex max-w-sm flex-col gap-3">
                  <button
                    onClick={addToCart}
                    disabled={adding}
                    className="rounded-lg bg-slate-900 px-5 py-3 font-bold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-orange-500 dark:text-slate-950"
                  >
                    {adding ? "Adding…" : "Add quote to cart"}
                  </button>
                  <Link
                    to={`/cutting-quote/${quote.review_token}/review`}
                    state={{ quote, email: form.email }}
                    className="rounded-lg border border-orange-500 px-4 py-3 text-sm font-bold text-orange-600 transition hover:bg-orange-50 dark:hover:bg-orange-950"
                  >
                    Need extra work on this part? Submit it for review
                  </Link>
                </div>
                <Link
                  to="/cart"
                  className="px-1 py-3 font-bold text-orange-600 hover:underline"
                >
                  View cart
                </Link>
              </div>
              {message && (
                <p className="mt-4 text-sm font-semibold text-green-600">
                  {message}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-6 bg-slate-950 px-6 py-10 md:grid-cols-4 md:px-10">
        {[
          ["A$20", "Minimum job"],
          [isPlasma ? "A$90/hr" : "A$100/hr", "Machine time"],
          ["A$80/hr", "CAD & design"],
          ["A$80/hr", "Finishing"],
        ].map(([price, label]) => (
          <div key={label} className="rounded-xl border border-white/10 p-5">
            <p className="text-2xl font-black text-orange-400">{price}</p>
            <p className="mt-1 text-sm text-slate-400">{label}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
